/**
 * Client-side bridge to the BookieBurglar Bet Agent browser extension.
 *
 * The extension (a separate MV3 Chrome project at `bet-agent-extension/`)
 * injects a content script into the BookieBrawler React app
 * (https://bookiebrawler.com in prod, http://localhost:3000 in dev)
 * that exposes a `window.__bbBetAgent` handshake object. We never call
 * `chrome.runtime.sendMessage` from page code directly — the page
 * has no permission to do that for arbitrary extension ids, and we
 * don't want to ship the extension id to every user. Instead the
 * content script bridges via {@code window.postMessage}, which works
 * regardless of extension id and is the same pattern Phantom /
 * MetaMask / 1Password use.
 *
 * Wire protocol on the {@code window.postMessage} channel:
 *
 *   {source:"bb-bet-agent", direction:"page->ext", type:..., payload:...}
 *   {source:"bb-bet-agent", direction:"ext->page", type:..., payload:...}
 *
 * The {@code direction} field exists so we don't echo our own
 * messages back to ourselves (postMessage fires on every frame in
 * the window, including the sender).
 */

import {
  AgentEvent,
  BetIntent,
  TERMINAL_STATES,
} from "./types";
import { signal } from "../../../../utils/signal";
import { currentUserHasBetAgentAccess } from "../../../login/hasAccess";

/* ─── Wire envelope ─── */

const WIRE_SOURCE = "bb-bet-agent" as const;

interface WireEnvelope {
  source: typeof WIRE_SOURCE;
  direction: "page->ext" | "ext->page";
  type:
    | "ping"
    | "pong"
    | "intent"
    | "event"
    | "abort"
    | "open-options"
    /**
     * Sent by the bridge content script when its `chrome.runtime`
     * handle has been invalidated (the extension was reloaded out
     * from under the page). The page reacts by setting
     * {@link bridgeStaleSignal} so the diagnostics card can prompt
     * the user to refresh the tab.
     */
    | "stale";
  payload?: unknown;
}

/* ─── Debug logging ─── */

/**
 * `[bb-bet-agent]`-prefixed logger gated by either
 *   - `?debug=1` in the URL, or
 *   - `localStorage.bb-bet-agent-debug = "1"`
 * so production users never see the noise. The same prefix is used
 * in the bridge content script and the extension service worker, so
 * a single DevTools console filter shows the full handshake chain.
 */
function isDebugEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.location.search.includes("debug=1")) return true;
    if (window.localStorage?.getItem("bb-bet-agent-debug") === "1") return true;
  } catch {
    /* private mode or quota errors — just stay quiet */
  }
  return false;
}

const DEBUG = isDebugEnabled();

function log(...args: unknown[]): void {
  if (DEBUG) console.log("[bb-bet-agent][page]", ...args);
}

/* ─── Extension presence detection ─── */

interface ExtensionInfo {
  installed: boolean;
  version?: string;
  /** When detection last completed. */
  detectedAt?: number;
}

export type DetectionStatus =
  | "idle"
  | "probing"
  | "connected"
  | "missing"
  | "stale"
  | "access-denied";

export type DetectionError =
  | "timeout"
  | "stale"
  | "access-denied"
  | "origin-mismatch-or-not-installed"
  | null;

/**
 * Live signal for "is the extension installed?". Components subscribe
 * via {@code useSignalValue} to render install prompts vs send
 * buttons. The signal updates within ~250ms of page load if the
 * extension's content script is present.
 */
export const betAgentSignal = signal<ExtensionInfo>({ installed: false });

/**
 * `true` when the bridge content script has reported its
 * `chrome.runtime` context is dead (extension was reloaded after
 * this tab loaded). UI surfaces this as a "refresh this tab to
 * reconnect" banner so users don't get a silent black hole. Resets
 * to `false` when the page reloads (the new bridge will be live).
 */
export const bridgeStaleSignal = signal<boolean>(false);
export const detectionStatusSignal = signal<DetectionStatus>("idle");
export const detectionErrorSignal = signal<DetectionError>(null);

let detectionGeneration = 0;
let detectionResolve: ((info: ExtensionInfo) => void) | null = null;
let detectionPromise:
  | { generation: number; promise: Promise<ExtensionInfo> }
  | null = null;
let messageListenerRegistered = false;

/* ─── Wire trace (diagnostics surface) ─── */

/**
 * One row in the wire trace ring buffer. Captures every envelope
 * crossing the page boundary in either direction so the diagnostics
 * card can show exactly where a handshake breaks.
 */
export interface WireTraceEntry {
  ts: number;
  direction: "page->ext" | "ext->page";
  type: WireEnvelope["type"];
  payloadSummary?: string;
}

const WIRE_TRACE_LIMIT = 50;

/**
 * Reactive ring buffer of recent wire envelopes. Empty until
 * {@link enableWireTrace} is called, after which {@link recordTrace}
 * pushes entries. The diagnostics card subscribes via
 * `useSignalValue` to render a live trace view.
 */
export const wireTraceSignal = signal<WireTraceEntry[]>([]);

let wireTraceEnabled = false;

/**
 * Turn on the wire trace ring buffer. Returns a tear-down function;
 * the trace clears when all subscribers tear down. Called from the
 * install/diagnostics page on mount so the trace populates as soon
 * as the user lands there.
 *
 * Idempotent — calling multiple times just nests the ref count so
 * mounting two diagnostics views simultaneously works.
 */
let traceRefcount = 0;
export function enableWireTrace(): () => void {
  traceRefcount++;
  wireTraceEnabled = true;
  return () => {
    traceRefcount = Math.max(0, traceRefcount - 1);
    if (traceRefcount === 0) {
      wireTraceEnabled = false;
      wireTraceSignal.value = [];
    }
  };
}

function recordTrace(entry: WireTraceEntry): void {
  if (!wireTraceEnabled && !DEBUG) return;
  const next = [...wireTraceSignal.peek(), entry];
  const trimmed =
    next.length > WIRE_TRACE_LIMIT
      ? next.slice(next.length - WIRE_TRACE_LIMIT)
      : next;
  wireTraceSignal.value = trimmed;
}

/**
 * Reset the in-memory detection cache so the next call to
 * {@link ensureBetAgentDetected} re-runs the handshake. Used by the
 * diagnostics "Re-test" button to retry without a full page reload.
 */
export function resetBetAgentDetection(): void {
  log("reset detection");
  detectionGeneration++;
  detectionResolve = null;
  detectionPromise = null;
  bridgeStaleSignal.value = false;
  detectionStatusSignal.value = "idle";
  detectionErrorSignal.value = null;
  betAgentSignal.value = { installed: false };
}

/**
 * Returns a promise that resolves to the current extension state.
 * The first call also kicks off the handshake; subsequent calls
 * return the cached promise. Resolves with {@code installed: false}
 * after a 1500ms timeout if the extension never replies.
 *
 * ADMIN-ONLY: short-circuits to {@code installed: false} for
 * non-admin users without ever posting the handshake. Belt-and-
 * braces with the UI gates so a stray caller can't leak the
 * surface to a paid-but-non-admin user.
 *
 * IMPORTANT: the access denial does NOT poison the cache. We
 * return a one-shot resolved promise but we don't store it as
 * `detectionPromise`. Otherwise an early call (e.g. from App.tsx
 * before the async `getCurrentUser()` resolves) would cache the
 * negative answer for the rest of the session, leaving a real
 * admin user permanently stuck on "extension not detected" even
 * after sign-in. The diagnostics page exposed exactly this bug.
 */
export function ensureBetAgentDetected(): Promise<ExtensionInfo> {
  const current = betAgentSignal.peek();
  if (current.installed && !bridgeStaleSignal.peek()) {
    return Promise.resolve(current);
  }
  if (detectionPromise) return detectionPromise.promise;
  if (!currentUserHasBetAgentAccess()) {
    log("ensureBetAgentDetected: access denied (not admin), skipping handshake");
    const info: ExtensionInfo = { installed: false, detectedAt: Date.now() };
    detectionStatusSignal.value = "access-denied";
    detectionErrorSignal.value = "access-denied";
    return Promise.resolve(info);
  }
  const generation = ++detectionGeneration;
  detectionStatusSignal.value = "probing";
  detectionErrorSignal.value = null;
  bridgeStaleSignal.value = false;
  const promise = new Promise<ExtensionInfo>((resolve) => {
    detectionResolve = resolve;
  });
  detectionPromise = { generation, promise };
  beginDetection(generation);
  return promise;
}

function beginDetection(generation: number) {
  if (typeof window === "undefined") return;
  registerMessageListenerOnce();

  // Send the handshake. If the extension's content script is
  // present, it will reply with a `pong` synchronously on the next
  // tick. If not, we resolve as "not installed" after 1.5s.
  const ping: WireEnvelope = {
    source: WIRE_SOURCE,
    direction: "page->ext",
    type: "ping",
  };
  log("posting ping at origin", window.location.origin);
  recordTrace({ ts: Date.now(), direction: "page->ext", type: "ping" });
  window.postMessage(ping, window.location.origin);

  setTimeout(() => {
    if (generation !== detectionGeneration) return;
    if (betAgentSignal.peek().installed) return;
    const info: ExtensionInfo = {
      installed: false,
      detectedAt: Date.now(),
    };
    log("ping timed out after 1500ms — extension not detected");
    betAgentSignal.value = info;
    detectionStatusSignal.value = "missing";
    detectionErrorSignal.value = "timeout";
    resolveCurrentDetection(generation, info);
  }, 1500);
}

function registerMessageListenerOnce(): void {
  if (messageListenerRegistered || typeof window === "undefined") return;
  messageListenerRegistered = true;
  window.addEventListener("message", handleWireMessage);
}

function handleWireMessage(e: MessageEvent): void {
  const env = e.data as WireEnvelope | undefined;
  if (
    !env ||
    env.source !== WIRE_SOURCE ||
    env.direction !== "ext->page"
  ) {
    return;
  }
  recordTrace({
    ts: Date.now(),
    direction: "ext->page",
    type: env.type,
    payloadSummary: summarizePayload(env.type, env.payload),
  });
  if (env.type === "pong") {
    const payload = env.payload as { version?: string } | undefined;
    const info: ExtensionInfo = {
      installed: true,
      version: payload?.version,
      detectedAt: Date.now(),
    };
    log("pong received, version=", payload?.version);
    bridgeStaleSignal.value = false;
    betAgentSignal.value = info;
    detectionStatusSignal.value = "connected";
    detectionErrorSignal.value = null;
    resolveCurrentDetection(detectionGeneration, info);
  } else if (env.type === "event") {
    const event = env.payload as AgentEvent | undefined;
    if (event) dispatchAgentEvent(event);
  } else if (env.type === "stale") {
    const reason =
      (env.payload as { reason?: string } | undefined)?.reason ??
      "unknown";
    log("bridge reported stale context:", reason);
    detectionGeneration++;
    bridgeStaleSignal.value = true;
    const info = { installed: false, detectedAt: Date.now() };
    betAgentSignal.value = info;
    detectionStatusSignal.value = "stale";
    detectionErrorSignal.value = "stale";
    detectionResolve?.(info);
    detectionResolve = null;
    detectionPromise = null;
  }
}

function resolveCurrentDetection(
  generation: number,
  info: ExtensionInfo
): void {
  if (detectionPromise?.generation !== generation) return;
  detectionResolve?.(info);
  detectionResolve = null;
  detectionPromise = null;
}

function summarizePayload(
  type: WireEnvelope["type"],
  payload: unknown,
): string | undefined {
  if (payload == null) return undefined;
  try {
    if (type === "pong") {
      const v = (payload as { version?: string }).version;
      return v ? `v${v}` : undefined;
    }
    if (type === "intent") {
      const intent = payload as BetIntent;
      const legCount = intent.legs?.length ?? 0;
      return `${intent.mode} ${legCount}leg ${intent.intentId.slice(0, 8)}`;
    }
    if (type === "event") {
      const ev = payload as AgentEvent;
      return `${ev.state} ${ev.intentId.slice(0, 8)}`;
    }
    if (type === "abort") {
      const id = (payload as { intentId?: string }).intentId;
      return id ? id.slice(0, 8) : undefined;
    }
  } catch {
    return undefined;
  }
  return undefined;
}

/* ─── Outbound: send a BetIntent ─── */

/**
 * Send a {@link BetIntent} to the extension. Resolves immediately
 * after the message is posted — the actual lifecycle is delivered
 * via {@link subscribeAgentEvents}. Throws if the extension isn't
 * installed; callers should gate with {@link ensureBetAgentDetected}.
 */
export async function sendBetIntent(intent: BetIntent): Promise<void> {
  if (!currentUserHasBetAgentAccess()) {
    throw new Error("Bet Agent is admin-only.");
  }
  const info = await ensureBetAgentDetected();
  const live = betAgentSignal.peek();
  if (!info.installed || !live.installed || bridgeStaleSignal.peek()) {
    throw new Error(
      bridgeStaleSignal.peek()
        ? "Bet Agent extension was reloaded. Refresh this page to reconnect."
        : "Bet Agent extension is not installed. Install it to add selections to sportsbook slips."
    );
  }
  const env: WireEnvelope = {
    source: WIRE_SOURCE,
    direction: "page->ext",
    type: "intent",
    payload: intent,
  };
  log("sending intent", intent.intentId, intent.mode, intent.legs.length, "leg(s)");
  recordTrace({
    ts: Date.now(),
    direction: "page->ext",
    type: "intent",
    payloadSummary: summarizePayload("intent", intent),
  });
  window.postMessage(env, window.location.origin);
}

/** Cancel an in-flight intent. The extension will move it to "aborted". */
export function abortBetIntent(intentId: string): void {
  if (!betAgentSignal.peek().installed) return;
  const env: WireEnvelope = {
    source: WIRE_SOURCE,
    direction: "page->ext",
    type: "abort",
    payload: { intentId },
  };
  log("aborting intent", intentId);
  recordTrace({
    ts: Date.now(),
    direction: "page->ext",
    type: "abort",
    payloadSummary: intentId.slice(0, 8),
  });
  window.postMessage(env, window.location.origin);
}

/** Open the extension's options page (cred entry, per-book toggles). */
export function openAgentOptions(): void {
  if (!betAgentSignal.peek().installed) return;
  const env: WireEnvelope = {
    source: WIRE_SOURCE,
    direction: "page->ext",
    type: "open-options",
  };
  log("requesting options page");
  recordTrace({ ts: Date.now(), direction: "page->ext", type: "open-options" });
  window.postMessage(env, window.location.origin);
}

/* ─── Inbound: agent state events ─── */

type AgentEventListener = (event: AgentEvent) => void;
const listeners = new Set<AgentEventListener>();

/**
 * Subscribe to lifecycle events for ALL active intents. Returns an
 * unsubscribe function. Filter by {@code event.intentId} in the
 * callback if you only care about a specific intent.
 */
export function subscribeAgentEvents(fn: AgentEventListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function dispatchAgentEvent(event: AgentEvent) {
  // Append to the rolling history (consumed by the status drawer).
  const next = [...agentHistorySignal.peek(), event];
  // Trim to a reasonable bound; only the last few intents matter.
  const trimmed = next.length > 200 ? next.slice(next.length - 200) : next;
  agentHistorySignal.value = trimmed;

  // Update the per-intent latest map.
  const map = new Map(activeIntentsSignal.peek());
  if (TERMINAL_STATES.has(event.state)) {
    map.set(event.intentId, event);
    // Auto-clear terminal entries after 8 seconds so the toast
    // disappears without the user having to dismiss it.
    setTimeout(() => {
      const m = new Map(activeIntentsSignal.peek());
      const latest = m.get(event.intentId);
      if (latest && latest.ts === event.ts) {
        m.delete(event.intentId);
        activeIntentsSignal.value = m;
      }
    }, 8000);
  } else {
    map.set(event.intentId, event);
  }
  activeIntentsSignal.value = map;

  listeners.forEach((fn) => {
    try {
      fn(event);
    } catch {
      // Listener errors should never break the dispatch loop.
    }
  });
}

/* ─── Reactive state for UI ─── */

/**
 * Map of {@code intentId → most recent AgentEvent}. The status
 * drawer renders one row per entry. Terminal entries auto-clear
 * after 8s (see {@link dispatchAgentEvent}).
 */
export const activeIntentsSignal = signal<Map<string, AgentEvent>>(
  new Map()
);

/**
 * Append-only history of every event the agent has emitted in this
 * tab. Useful for a "view full log" drawer — capped to last 200.
 */
export const agentHistorySignal = signal<AgentEvent[]>([]);
